/**
 * D3 — a lotação agregada da campanha, no topo da página: `CapacityMeter` + "312 de 2.000
 * vagas livres". Tudo contado no servidor (`campaign.capacity`); aqui só a frase.
 *
 * Estados: carregando = skeleton no formato final; sem grupo com contagem = "—" com o motivo
 * (nunca "0 vagas"); erro = some em silêncio para não tampar a página (o resto da campanha
 * continua útil).
 */
import { CapacityMeter, NO_DATA_DASH, Skeleton, formatNumber } from '@mgdlead/ui';

import { useCampaignCapacity, type CampaignCapacity } from '../../lib/api';

export function capacityCaption(cap: CampaignCapacity): string {
  const vagas = `${formatNumber(cap.seatsLeft)} de ${formatNumber(cap.capacity)} vagas livres`;
  const semContagem =
    cap.groupsWithoutCount > 0
      ? ` · ${cap.groupsWithoutCount} ${cap.groupsWithoutCount === 1 ? 'grupo' : 'grupos'} sem contagem`
      : '';
  return `${vagas}${semContagem}`;
}

export function CampaignCapacityLine({
  workspaceId,
  campaignId,
}: {
  workspaceId: string;
  campaignId: string;
}) {
  const query = useCampaignCapacity(workspaceId, campaignId);

  if (query.isPending) return <Skeleton className="h-5 w-64" />;
  if (query.isError) return null;

  const cap = query.data?.[0];
  if (cap === undefined) return null;

  if (cap.groupsCount === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <span aria-hidden="true">{NO_DATA_DASH}</span> Lotação: nenhum grupo vinculado ainda.
      </p>
    );
  }

  if (cap.occupancy === null) {
    return (
      <p className="text-sm text-muted-foreground">
        <span aria-hidden="true">{NO_DATA_DASH}</span> Lotação: os grupos ainda não foram contados.
        Atualize as contagens na tela de Grupos.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <CapacityMeter
        value={cap.occupancy}
        label="Lotação da campanha"
        caption={capacityCaption(cap)}
      />
      {cap.seatsInRotation === null ? (
        <span className="text-2xs text-subtle-foreground">
          <span aria-hidden="true">{NO_DATA_DASH}</span> Vagas no rodízio: os grupos que recebem
          gente agora ainda não foram contados.
        </span>
      ) : cap.seatsInRotation !== cap.seatsLeft || cap.rotationWithoutCount > 0 ? (
        <span className="nums text-2xs text-subtle-foreground">
          {formatNumber(cap.seatsInRotation)} recebendo gente agora (grupos ativos e com convite)
          {cap.rotationWithoutCount > 0
            ? ` · ${cap.rotationWithoutCount} ${cap.rotationWithoutCount === 1 ? 'grupo' : 'grupos'} do rodízio sem contagem`
            : ''}
        </span>
      ) : null}
    </div>
  );
}
